import type { User } from '../../../server/websocket/user/edit.no-deps'
import { difference, uniq } from 'remeda'
import { xor } from 'lodash'
import type { NestedTeam } from '../teams/types.no-deps'
import { flatIdsAll } from '../teams/helpers'
import type { CamelCaseFlags } from '../../cfg/flags.no-deps'

// DEPRECATED - DO not add any more permissions to these. Use PortalPermissions only.
// DEPRECATED - PortalPermissions will be used for both app and portal in the future
export type AppPermissions =
    | 'recommendation'
    | 'addLocations'
    | 'smartData'
    | 'gamification'
    | 'planner'
    | 'manageLocations'
    | 'superUser'

// DEPRECATED - DO not add any more permissions to these. Use PortalPermissions only.
// DEPRECATED - PortalPermissions will be used for both app and portal in the future
const legacyAppPermissions: AppPermissions[] = [
    'recommendation',
    'addLocations',
    'smartData',
    'gamification',
    'planner',
    'manageLocations',
]

// DEPRECATED - DO not add any more permissions to these. Use PortalPermissions only.
// DEPRECATED - PortalPermissions will be used for both app and portal in the future
export const appPermissions: AppPermissions[] = []

export const allAppPermissions: AppPermissions[] = [...appPermissions, ...legacyAppPermissions]

export type PortalPermissions =
    | 'process'
    | 'processEdit'
    // Can manage procedures that the user have access to, e.g. via collaboration
    | 'procedureManagement'
    | 'userManagement'
    // For legacy users. Allows to see campaigns tab and data management. Tab under units/procedures is called “Files”
    | 'businessManagement'
    | 'analytics'
    // can manage things like Units
    | 'dataManagement'
    // Can manage any Procedure
    | 'procedureAdmin'
    | 'superUser'
    | 'confidentialReports'
    | 'aiFunctionality'
    | 'aiReportsAccess'
    // this permission is vague on purpose as it limits a user from doing various _things_.
    // Without a proper role based extensive permission system unfortunately this is the best we can do
    // for now.
    | 'restrictedUser'

const legacyPortalPermissions: PortalPermissions[] = ['process', 'processEdit', 'businessManagement']

export const portalPermissions: PortalPermissions[] = [
    'procedureManagement',
    'userManagement',
    'analytics',
    'dataManagement',
    'procedureAdmin',
    'superUser',
    'confidentialReports',
    'aiFunctionality',
    'aiReportsAccess',
    'restrictedUser',
]

export const allPortalPermissions: PortalPermissions[] = [...portalPermissions, ...legacyPortalPermissions]

export interface UserPermissions {
    app: AppPermissions[]
    portal: PortalPermissions[]
}

export type Permission = PortalPermissions

export function canUserManageTeams(adminTeams: Set<number>, teamsToAdd: number[]) {
    for (const team of teamsToAdd) {
        if (!adminTeams.has(team)) return false
    }
    return true
}

export function doesUserHavePermission(
    flags = {} as CamelCaseFlags,
    userPermissions: UserPermissions,
    permission: PortalPermissions
): boolean {
    if (permission === 'restrictedUser' && !flags.procedureBubbles) {
        return false
    }
    if (permission === 'confidentialReports' && !flags.confidentialReports) {
        return false
    }
    // some permissions are not granted purely because of the user being a superUser
    if (
        isSuperUser(userPermissions) &&
        permission !== 'restrictedUser' &&
        permission !== 'confidentialReports' &&
        permission !== 'aiFunctionality' &&
        permission !== 'aiReportsAccess'
    ) {
        return true
    }
    if (permission === 'aiReportsAccess' && !userPermissions.portal.includes('aiFunctionality')) {
        return false
    }
    if (permission === 'procedureManagement' && doesUserHavePermission(flags, userPermissions, 'procedureAdmin')) {
        return true
    }
    return userPermissions.portal.includes(permission)
}

export function canUserEditOtherUserPermissions(
    editingUserPermissions: UserPermissions,
    otherUserNewPermissions: UserPermissions,
    otherUserOriginalPermissions: UserPermissions
): boolean {
    const permissionsEditingUserCanManage = permissionsThatUserCanManage(editingUserPermissions)

    const otherUserNewAddedPermissions = {
        app: difference(otherUserNewPermissions.app, otherUserOriginalPermissions.app),
        portal: difference(otherUserNewPermissions.portal, otherUserOriginalPermissions.portal),
    }
    const otherUserRemovedPermissions = {
        app: difference(otherUserOriginalPermissions.app, otherUserNewPermissions.app),
        portal: difference(otherUserOriginalPermissions.portal, otherUserNewPermissions.portal),
    }
    const otherUserChangedPermissions = {
        app: [...otherUserNewAddedPermissions.app, ...otherUserRemovedPermissions.app],
        portal: [...otherUserNewAddedPermissions.portal, ...otherUserRemovedPermissions.portal],
    }
    return (
        otherUserChangedPermissions.portal.every((permission) =>
            permissionsEditingUserCanManage.portal.includes(permission)
        ) &&
        otherUserChangedPermissions.app.every((permission) => permissionsEditingUserCanManage.app.includes(permission))
    )
}

export function permissionsThatUserCanManage(userPermissions: UserPermissions): UserPermissions {
    return {
        portal: isSuperUser(userPermissions)
            ? uniq(portalPermissions.concat(userPermissions.portal))
            : userPermissions.portal.filter(
                  (permission) => !permissionsThatOnlySuperUserCanManage.includes(permission)
              ),

        app: isSuperUser(userPermissions) ? uniq(appPermissions.concat(userPermissions.app)) : userPermissions.app,
    }
}

const permissionsThatOnlySuperUserCanManage = [
    'confidentialReports',
    'aiFunctionality',
    'aiReportsAccess',
    'restrictedUser',
]

export function canUserManagePermission(
    userPermissions: UserPermissions,
    permission: PortalPermissions | AppPermissions
): boolean {
    const permissionsThatCanManage = permissionsThatUserCanManage(userPermissions)
    return (
        permissionsThatCanManage.portal.includes(permission as PortalPermissions) ||
        permissionsThatCanManage.app.includes(permission as AppPermissions)
    )
}

type Teams = { administratedTeams: NestedTeam[]; teams: NestedTeam[] }

// Checks that all the teams that are added or removed to the initial teams are part of the origin's administrated teams
// Only used and tested in the context of user.edit, if it needs to be used elsewhere we will need to provide tests
export async function canEditTeams(
    user: User,
    origin: Teams,
    target: { teams: number[]; administratedTeams: number[] },
    initial: Teams
) {
    if (isSuperUser(user.privs)) {
        return true
    }

    // Teams of the admin
    const originAdministratedTeams = flatIdsAll(origin.administratedTeams)

    // Find out what changed - regardless of weather it is being removed or added
    const teamsDiff = xor([...flatIdsAll(initial.teams)], target.teams)
    const adminTeamsDiff = xor([...flatIdsAll(initial.administratedTeams)], target.administratedTeams)

    // Check that all changes made to the teams are in the administrated teams
    return (
        teamsDiff.every((team) => originAdministratedTeams.has(team)) &&
        adminTeamsDiff.every((team) => originAdministratedTeams.has(team))
    )
}

export function isSuperUser(userPermissions: UserPermissions): boolean {
    return userPermissions.portal.includes('superUser')
}

export function isRestrictedUser(userPermissions: UserPermissions): boolean {
    return userPermissions.portal.includes('restrictedUser')
}

export function isLegacyUser(userPermissions: UserPermissions): boolean {
    return (
        userPermissions.portal.includes('businessManagement') && !userPermissions.portal.includes('procedureManagement')
    )
}
